import debounce from "lodash.debounce";
import { ChangeEventHandler, useCallback, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

import { checkAvailability } from "~/api/auth/checkAvailability";
import ApplicationInput from "~/components/application/input";
import ApplicationRulesOfSilencio from "~/components/application/ruleOfSilencio";
import { useLanguage } from "~/contexts/providers/language";
import englishTranslations from "~/localization/en";
import frenchTranslations from "~/localization/fr";
import { CreateMemberInput } from "~/types/user/user.types";
import { emailRegex } from "~/utils/constants/regex";

type Props = {
  passwordText: string;
  handleSetPasswordText: (text: string) => void;
};

const ApplicationAboutYouLogin = ({
  passwordText,
  handleSetPasswordText,
}: Props) => {
  const { setFocus } = useFormContext<CreateMemberInput>();
  const [emailText, setEmailText] = useState("");
  const { language } = useLanguage();
  const applyTexts =
    language === "En"
      ? englishTranslations.applyPage
      : frenchTranslations.applyPage;

  const handleSetEmailText = useCallback(
    async (email: string) => {
      const available = await checkAvailability(email);
      if (!available) {
        setEmailText(
          language === "En"
            ? "This email is already linked to an account"
            : "Cet email est déjà rattaché à un compte"
        );
        setTimeout(() => {
          setFocus("email");
        }, 10);
      } else {
        setEmailText("");
      }
    },
    [setFocus, language]
  );

  const debouncedhandleSetEmailText = useMemo(
    () => debounce(handleSetEmailText, 300),
    [handleSetEmailText]
  );

  const handleSetPasswordChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    if (e.target.value !== "") {
      handleSetPasswordText("");
    }
  };

  return (
    <>
      <div>
        <p>{applyTexts.applicationForm.login}</p>
        <ApplicationInput
          register="email"
          placeholder="Email"
          label="Email"
          required={true}
          size={18}
          options={{
            pattern: emailRegex,
          }}
          onChange={(e) => {
            debouncedhandleSetEmailText(e.target.value);
          }}
        />
        <p>{emailText}</p>
        <ApplicationInput
          register="password"
          placeholder={applyTexts.applicationForm.password}
          label={applyTexts.applicationForm.password}
          type="password"
          size={20}
          required={true}
          onChange={handleSetPasswordChange}
        />
        <p>{passwordText}</p>
        <ApplicationInput
          register="confirmPassword"
          placeholder={`${applyTexts.applicationForm.confirmPassword}*`}
          label={`${applyTexts.applicationForm.confirmPassword}*`}
          type="password"
          size={22}
        />
      </div>
      <p>
        *{applyTexts.applicationForm.dataStore}{" "}
        <ApplicationRulesOfSilencio
          text={`→ ${applyTexts.applicationForm.privacyPolicy}`}
        />
      </p>
    </>
  );
};

export default ApplicationAboutYouLogin;
